import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { captureRequestId } from './telemetry';
import { wasRecentlyInteractive } from './interactionSignal';
import { CSRF_HEADER, getCsrfToken } from './csrf';

declare module 'axios' {
  // #1008: a request flagged `silent` whose 401 must NOT trigger
  // forceLogoutRedirect — used by AuthContext's mount-time refresh probe,
  // which runs under AuthProvider on public routes (/home, /privacy, /login)
  // where a hard redirect to /login would break anonymous rendering.
  interface AxiosRequestConfig {
    silent?: boolean;
    // #1220: set true on axios calls that are known background traffic (token
    // refresh) so the interaction tagger never marks them, even if a real
    // gesture happens to be recent.
    background?: boolean;
  }
}

/** #1220: header the backend reads to bump the engagement signal. */
export const INTERACTION_HEADER = 'X-User-Interaction';

/**
 * Request interceptor (#1220): tag genuinely user-initiated requests so the
 * backend bumps "Last Active" / WAU only on real interaction. A request counts
 * as interactive when it follows a real gesture within `INTERACTION_WINDOW_MS`
 * (see `interactionSignal`) and is not explicitly flagged `background`.
 * Background timers (refresh / React-Query refetch / polling) fire with no
 * recent gesture → untagged; SSE/EventSource never uses axios. Exported so the
 * tag/never-tag matrix can be unit-tested directly.
 */
export function tagInteractiveRequest(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.background && wasRecentlyInteractive()) {
    config.headers.set(INTERACTION_HEADER, 'true');
  }
  return config;
}

/** Methods the BFF treats as unsafe (CSRF-validated unconditionally since #1337). */
const CSRF_UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Request interceptor (#1217 D1): echo the BFF-minted `csrf_token` cookie as
 * the `X-CSRF-Token` header on unsafe methods (double-submit cookie). The BFF
 * validates header == cookie unconditionally (the `BFF_ENFORCE_CSRF` leash was
 * retired in #1337). No-op when the cookie is absent (e.g. before the first BFF
 * response mints it) so it never sends an empty token. Exported for unit testing.
 */
export function attachCsrfToken(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const method = (config.method ?? 'get').toLowerCase();
  if (CSRF_UNSAFE_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) {
      config.headers.set(CSRF_HEADER, token);
    }
  }
  return config;
}

// API URL - uses /api prefix which BFF translates to /api/v1
const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for HttpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — no manual token handling (HttpOnly cookies ride along
// with withCredentials). #1220: tag genuine user interactions so the backend
// de-noises the "Last Active" / WAU signal.
api.interceptors.request.use(
  tagInteractiveRequest,
  (error) => {
    return Promise.reject(error);
  }
);

// #1217 D1: echo the double-submit CSRF token on unsafe methods.
api.interceptors.request.use(
  attachCsrfToken,
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Force a logout: clear expiry hint, set the session-expired flag the
 * /login page reads, and navigate. Extracted (#960) so the response
 * interceptor's logic can call it from both the "refresh returned 401"
 * branch and the "401 on /auth/refresh itself" branch without
 * duplication.
 */
function forceLogoutRedirect(): void {
  localStorage.removeItem('token_expiry');
  if (window.location.pathname !== '/account-deleted') {
    sessionStorage.setItem('session_expired', 'true');
    window.location.href = '/login';
  }
}

/**
 * Response-interceptor error handler. Exported so unit tests can
 * exercise the branching directly (axios's adapter contract is hard
 * to drive through a real ``api.get`` round-trip; testing the
 * handler in isolation is cleaner and tracks the actual decision
 * surface this PR is meant to fix). Returns the retry promise on
 * refresh-success; otherwise a rejected promise carrying the
 * appropriate error.
 *
 * #960: the ``catch (refreshError)`` branch differentiates
 * HTTP-401-from-refresh-response (→ logout) vs every other rejection
 * cause (→ bubble up; do NOT log out). The #950 RCA established
 * every production auto-logout instance was a transient cause, not a
 * server-side token rejection.
 */
export async function handleResponseError(error: AxiosError): Promise<unknown> {
  // Capture even on error responses — failed requests still count
  // toward the slow-load picture.
  captureRequestId(error.response?.headers?.['x-request-id'] as string | undefined);

  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // Don't redirect if user is on account-deleted page (account was just deleted)
  if (window.location.pathname === '/account-deleted') {
    return Promise.reject(error);
  }

  const originalRequest = error.config;

  if (originalRequest && !originalRequest.url?.includes('/auth/refresh')) {
    try {
      // Import here to avoid circular dependency
      const { authService } = await import('./authService');
      await authService.refreshToken();
      // Retry original request
      return api(originalRequest);
    } catch (refreshError) {
      // #960: Only redirect to /login when the refresh call genuinely
      // returned HTTP 401. Transient causes (network error, 5xx,
      // request cancelled, JS exception) must NOT log the user out.
      const refreshStatus = (refreshError as AxiosError | undefined)?.response?.status;
      if (refreshStatus === 401) {
        forceLogoutRedirect();
      }
      return Promise.reject(refreshError);
    }
  }

  // A silent probe (AuthContext's mount-time rehydration) must not force a
  // hard /login redirect — it runs on public routes too. Bubble the 401 and
  // let the route guards decide (protected → soft Navigate to /login; public
  // → render anonymously). #1008
  if (originalRequest?.silent) {
    return Promise.reject(error);
  }

  // Already a refresh request (i.e. /auth/refresh itself returned
  // 401) or no original request to retry — that IS a genuine
  // refresh-token rejection, so log out.
  forceLogoutRedirect();
  return Promise.reject(error);
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Capture backend X-Request-ID off every response so the page-load
    // telemetry hook can correlate slow loads back to backend timings
    // in ELK (issue #643).
    captureRequestId(response.headers?.['x-request-id'] as string | undefined);
    return response;
  },
  handleResponseError
);

export default api;
